import { ChangeEvent, useEffect, useRef, useState } from "react";

const HEIGHT_EPSILON_PX = 1;

type Params = {
  value: string;
  onValueChange: (value: string) => void;
};

export const useAutosizeTextarea = ({ value, onValueChange }: Params) => {
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const referenceRef = useRef<HTMLDivElement | null>(null);
  const isMultilineRef = useRef(false);
  const lastFailedCollapseLengthRef = useRef<number | null>(null);
  const recheckRef = useRef<() => void>(() => {});
  const [isMultiline, setIsMultilineState] = useState(false);

  const setIsMultiline = (next: boolean) => {
    isMultilineRef.current = next;
    setIsMultilineState(next);
  };

  useEffect(() => {
    const mirror = mirrorRef.current;
    const reference = referenceRef.current;
    if (!mirror || !reference) return;

    const fitsOneRow = (height: number) => {
      const singleLineHeight = reference.getBoundingClientRect().height;
      return singleLineHeight > 0 && height <= singleLineHeight + HEIGHT_EPSILON_PX;
    };

    // Read from the mirror rather than the value prop, so the length always describes the
    // same committed text whose height is being measured in the same callback.
    const renderedLength = () => mirror.textContent?.length ?? 0;

    const verifyCollapse = () => {
      if (!fitsOneRow(mirror.getBoundingClientRect().height)) {
        lastFailedCollapseLengthRef.current = renderedLength();
        setIsMultiline(true);
      }
    };

    const recheck = (height: number) => {
      if (!isMultilineRef.current) {
        if (!fitsOneRow(height)) setIsMultiline(true);
        return;
      }

      const canAttemptCollapse =
        lastFailedCollapseLengthRef.current === null ||
        renderedLength() < lastFailedCollapseLengthRef.current;

      if (fitsOneRow(height) && canAttemptCollapse) {
        setIsMultiline(false);
        requestAnimationFrame(verifyCollapse);
      }
    };

    recheckRef.current = () => recheck(mirror.getBoundingClientRect().height);

    const observer = new ResizeObserver(([entry]) => {
      const height =
        entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      if (height === 0) return;
      recheck(height);
    });
    observer.observe(mirror);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => recheckRef.current());
  }, [value]);

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onValueChange(e.target.value);
  };

  return {
    mirrorRef,
    referenceRef,
    isMultiline,
    onChange,
  };
};
